import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { orders } from '@/utils/api';
import { toast } from 'sonner';
import { Package, Truck, CheckCircle, Clock, Download } from 'lucide-react';

const BuyerDashboard = () => {
  const navigate = useNavigate();
  const [orderList, setOrderList] = useState([]);
  const [loading, setLoading] = useState(true);
  const user = JSON.parse(localStorage.getItem('user') || 'null');

  useEffect(() => {
    if (!user || user.userType !== 'buyer') {
      toast.error('Access denied');
      navigate('/login');
      return;
    }
    loadOrders();
  }, []);

  const loadOrders = async () => {
    try {
      const response = await orders.getBuyerOrders();
      setOrderList(response.data);
    } catch (error) {
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Clock className="w-5 h-5 text-yellow-600" />;
      case 'confirmed': return <Package className="w-5 h-5 text-blue-600" />;
      case 'shipped': return <Truck className="w-5 h-5 text-purple-600" />;
      case 'delivered': return <CheckCircle className="w-5 h-5 text-green-600" />;
      default: return <Clock className="w-5 h-5" />;
    }
  };

  const handleDownloadInvoice = async (orderId) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${backendUrl}/api/orders/${orderId}/invoice`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `invoice_${orderId}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('Invoice downloaded successfully');
      } else {
        toast.error('Invoice not available yet');
      }
    } catch (error) {
      toast.error('Failed to download invoice');
    }
  };

  return (
    <div data-testid="buyer-dashboard">
      <Navbar />
      
      <div style={{paddingTop: "180px"}}>
        <section className="section">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="section-title" data-testid="dashboard-title">My Orders</h1>
              <p className="section-subtitle" data-testid="dashboard-subtitle">
                Track and manage your orders
              </p>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-12">Loading orders...</div>
          ) : orderList.length > 0 ? (
            <div className="max-w-4xl mx-auto space-y-4" data-testid="orders-list">
              {orderList.map((order) => (
                <div key={order.id} className="card" data-testid={`order-${order.id}`}>
                  <div className="flex flex-col md:flex-row justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-semibold">{order.productTitle}</h3>
                        {getStatusIcon(order.status)}
                      </div>
                      <p className="text-sm text-gray-600">Order ID: {order.orderId}</p>
                      <p className="text-sm text-gray-600">Quantity: {order.quantity}</p>
                      <p className="text-sm text-gray-600">
                        Date: {new Date(order.createdAt).toLocaleDateString()}
                      </p>
                      {order.trackingNumber && (
                        <p className="text-sm text-gray-600 mt-2">
                          <strong>Tracking:</strong> {order.trackingNumber}
                        </p>
                      )}
                      {order.shippingAddress && (
                        <p className="text-sm text-gray-600 mt-2">
                          <strong>Shipping:</strong> {order.shippingAddress}
                        </p>
                      )}
                    </div>
                    <div className="text-right flex flex-col items-end gap-2">
                      <span className={`badge badge-${order.status}`}>
                        {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                      </span>
                      <p className="text-2xl font-bold text-[#ef4444]">
                        ₹{order.totalAmount.toLocaleString()}
                      </p>
                      {order.invoiceUrl && (
                        <Button
                          onClick={() => handleDownloadInvoice(order.orderId)}
                          variant="outline"
                          size="sm"
                          className="mt-2"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Download Invoice
                        </Button>
                      )}
                      {order.buyerGstNumber && (
                        <p className="text-xs text-gray-500 mt-1">
                          GST: {order.buyerGstNumber}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12" data-testid="no-orders">
              <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">No orders yet</p>
              <p className="text-gray-400 mt-2">Start shopping to see your orders here</p>
            </div>
          )}
        </section>
      </div>

      <Footer />
    </div>
  );
};

export default BuyerDashboard;